/* eslint-disable no-console */
import { useEffect, useState } from 'react';

/**
 * Build-time mock flag.
 *
 * This MUST read `import.meta.env.*` directly instead of the `@/env/env` object. Vite
 * replaces a raw `import.meta.env.VITE_*` member expression with a string literal at build
 * time, so `MOCK_MODE_ENABLED` folds to a constant and Rollup dead-code-eliminates the entire
 * `@/mocks/browser` graph — MSW runtime, every handler, every fake fixture — out of any
 * build that does not set the flag. Reading `env.VITE_MOCK_MODE` hides the value behind a
 * `createEnv` object property that Rollup cannot fold, which shipped an ~836 kB mock chunk
 * (fake vessels and documents included) into every production bundle.
 *
 * Mock data must never reach real production. Keep this literal literal.
 */
const MOCK_MODE_ENABLED = import.meta.env.VITE_MOCK_MODE === 'true';

export const useMockServiceWorker = () => {
  const [isReady, setIsReady] = useState(!MOCK_MODE_ENABLED);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!MOCK_MODE_ENABLED) {
      return;
    }
    async function initMSW() {
      try {
        const { worker } = await import('@/mocks/browser');

        await worker.start({
          onUnhandledRequest: 'warn',
          quiet: false,
          serviceWorker: {
            url: '/mockServiceWorker.js'
          }
        });

        console.log('🔶 Mock mode enabled - MSW intercepting API requests');
        setIsReady(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to start MSW');
        console.error('❌ Failed to start MSW:', error);
        setError(error);
        setIsReady(true);
      }
    }

    void initMSW();
  }, []);

  return { isReady, error };
};
